import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Sparkles, Image, Clock, Crown, ArrowRight, Shirt } from "lucide-react";
import { Progress } from "../components/ui/progress";

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refreshUser();
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/try-on/history`, {
        withCredentials: true
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const generationsLeft = user?.is_subscribed ? 50 - (user?.generations_used || 0) : (user?.free_tries_used >= 3 ? 0 : 3 - (user?.free_tries_used || 0));
  const progressPercent = user?.is_subscribed ? ((user?.generations_used || 0) / 50) * 100 : ((user?.free_tries_used || 0) / 3) * 100;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="dashboard-page">
      <Navbar />
      
      <main className="pt-24 md:pt-32 pb-24">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-12">
            <p className="caption text-[#FF1493] mb-2">Dashboard</p>
            <h1 className="heading-lg">
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Subscription Status */}
            <div className="card-dashboard" data-testid="subscription-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#FF1493]/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-[#FF1493]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium text-white">
                    {user?.is_subscribed ? "Pro Member" : "Free Tier"}
                  </p>
                </div>
              </div>
              {!user?.is_subscribed && (
                <Link to="/subscription">
                  <button className="btn-primary w-full text-sm h-10">
                    Upgrade to Pro
                  </button>
                </Link>
              )}
              {user?.is_subscribed && user?.subscription_end && (
                <p className="text-sm text-gray-500">
                  Renews: {new Date(user.subscription_end).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Generations */}
            <div className="card-dashboard" data-testid="generations-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#FF1493]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#FF1493]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Generations Left</p>
                  <p className="font-medium text-white">{generationsLeft} remaining</p>
                </div>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <p className="text-xs text-gray-500 mt-2">
                {user?.is_subscribed 
                  ? `${user?.generations_used || 0} of 50 used this month`
                  : user?.free_tries_used >= 3 
                    ? "Free trials used"
                    : `${3 - (user?.free_tries_used || 0)} free try-ons available`
                }
              </p>
            </div>

            {/* Quick Action */}
            <div className="card-dashboard" data-testid="quick-action-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#FF1493]/20 flex items-center justify-center">
                  <Shirt className="w-5 h-5 text-[#FF1493]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ready to Try?</p>
                  <p className="font-medium text-white">Start Creating</p>
                </div>
              </div>
              <Link to="/studio">
                <button className="btn-secondary w-full text-sm h-10">
                  Open Studio
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>

          {/* History Section */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="heading-md">Your Try-Ons</h2>
              {history.length > 0 && (
                <Link to="/studio" className="text-[#FF1493] text-sm font-medium hover:underline">
                  Create New
                </Link>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 spinner"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-16 bg-[#141414] border border-white/10" data-testid="empty-history">
                <Image className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="font-heading text-xl mb-2 text-white">No try-ons yet</h3>
                <p className="text-gray-400 mb-6">
                  Start your first virtual try-on experience
                </p>
                <Link to="/studio">
                  <button className="btn-primary">
                    Go to Studio
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="history-grid">
                {history.map((item) => (
                  <Link 
                    key={item.result_id} 
                    to={`/result/${item.result_id}`}
                    className="group"
                    data-testid={`history-item-${item.result_id}`}
                  >
                    <div className="card-product aspect-[3/4] mb-3">
                      <img
                        src={`data:image/png;base64,${item.result_image}`}
                        alt={item.garment_name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-[#FF1493]/10 transition-colors"></div>
                    </div>
                    <h3 className="font-medium text-sm text-white">{item.garment_name}</h3>
                    <div className="flex items-center gap-2 text-gray-500 text-xs mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
